import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useScrollSnap } from '../hooks/useScrollSnap';
import {
  IonContent,
  IonCard,
  IonCardContent,
  IonItem,
  IonLabel,
  IonIcon,
  IonChip,
  IonSpinner,
  IonAlert,
  IonToast,
  IonTextarea,
  IonButton,
  IonGrid,
  IonToggle,
} from '@ionic/react';
import {
  callOutline,
  trashOutline,
  chevronBack,
  addOutline,
  personOutline,
  createOutline,
  closeOutline,
  mailOutline,
  ellipsisVertical,
} from 'ionicons/icons';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { dbTwilio } from '../auth/firebase';
import { useAuth } from '../contexts';
import { isOwnerRole, isManagerRole } from '@rsmaids/shared/constants/roles';
import BasePageWithHeader, { TitleCard } from '../components/BasePageWithHeader';
import PhoneAgentInput from '../components/PhoneAgentInput';
import styles from './IvrAgents.module.scss';
import { useTranslation } from 'react-i18next';

interface IvrAgentsProps {
  onClose?: () => void;
}

interface Agent {
  sid: string;
  name: string;
  phone: string | null;
  email: string | null;
  clientId: string | null;
  available: boolean;
  activityName: string;
  isSelf?: boolean;
}

const IvrAgents: React.FC<IvrAgentsProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  // Owners, admins and managers run the whole phone setup; staff and leads can only switch their own agent on or off
  const canManageAll = isOwnerRole(authState.user?.role) || isManagerRole(authState.user?.role);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [toastMsg, setToastMsg] = useState('');
  const [deleteAlert, setDeleteAlert] = useState<Agent | null>(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newEmail, setNewEmail] = useState('');

  const [saving, setSaving] = useState(false);

  const [editAgent, setEditAgent] = useState<Agent | null>(null);
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editEmail, setEditEmail] = useState('');

  const [editSaving, setEditSaving] = useState(false);

  const [greeting, setGreeting] = useState('');
  const [voicemailPrompt, setVoicemailPrompt] = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [editingGreeting, setEditingGreeting] = useState(false);
  const [editingVoicemail, setEditingVoicemail] = useState(false);
  const [openMenuSid, setOpenMenuSid] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);

  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const titleCardRef = useRef<HTMLIonCardElement>(null);
  const gridRef = useRef<HTMLIonGridElement>(null);
  const pickerPortalRef = useRef<HTMLDivElement>(null);
  const [initialGreeting, setInitialGreeting] = useState('');
  const [initialVoicemailPrompt, setInitialVoicemailPrompt] = useState('');

  const functions = getFunctions();

  const loadAgents = useCallback(async () => {
    setLoading(true);
    try {
      const result = await httpsCallable(functions, 'listAgents')({});
      setAgents((result.data as { agents: Agent[] }).agents);
    } catch {
      setToastMsg(t('pages.IvrAgents.failedToLoadAgents'));
    } finally {
      setLoading(false);
    }
  }, [functions, t]);

  useEffect(() => { Promise.resolve().then(() => loadAgents()); }, [loadAgents]);

  useEffect(() => {
    getDoc(doc(dbTwilio, 'settings', 'ivr')).then(snap => {
      const d = snap.data() || {};
      const loadedGreeting = d.greeting || '';
      const loadedVoicemailPrompt = d.voicemailPrompt || '';
      setInitialGreeting(loadedGreeting);
      setInitialVoicemailPrompt(loadedVoicemailPrompt);
      setGreeting(loadedGreeting);
      setVoicemailPrompt(loadedVoicemailPrompt);
    }).catch(() => {});
  }, []);

  const applyProgress = useCallback((progress: number) => {
    if (titleCardRef.current) {
      titleCardRef.current.style.setProperty('--title-opacity', String(1 - progress * 3));
      titleCardRef.current.style.setProperty('--content-opacity', String(1 - progress * 3));
    }
    if (baseHeaderRef.current) {
      baseHeaderRef.current.style.setProperty('--title-opacity', String(Math.max(0, progress * 3 - 1)));
    }
  }, []);

  const handleScroll = useScrollSnap(gridRef, 66, applyProgress);

  const handleToggle = async (agent: Agent, nextAvailable?: boolean) => {
    const next = typeof nextAvailable === 'boolean' ? nextAvailable : !agent.available;
    try {
      await httpsCallable(functions, 'setAgentAvailability')({ workerSid: agent.sid, available: next });
      setAgents(prev => prev.map(a => a.sid === agent.sid ? { ...a, available: next } : a));
    } catch {
      setToastMsg(t('pages.IvrAgents.failedToUpdateAvailability'));
    }
  };

  const handleDelete = async (agent: Agent) => {
    try {
      await httpsCallable(functions, 'deleteAgent')({ workerSid: agent.sid });
      setAgents(prev => prev.filter(a => a.sid !== agent.sid));
      setToastMsg(t('pages.IvrAgents.removed', { name: agent.name }));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setToastMsg(t('pages.IvrAgents.deleteFailed', { msg }));
    }
    setDeleteAlert(null);
  };

  const handleAdd = async () => {
    if (!newName.trim() || !newPhone.trim() || !newEmail.trim()) {
      setToastMsg(t('pages.IvrAgents.namePhoneAndLoginEmailAre'));
      return;
    }
    setSaving(true);
    try {
      const result = await httpsCallable(functions, 'createAgent')({
        name: newName.trim(), phone: newPhone.trim(),
        email: newEmail.trim(),
      });
      setAgents(prev => [...prev, result.data as Agent]);
      setNewName(''); setNewPhone(''); setNewEmail('');
      setShowAddForm(false);
      setToastMsg(`${(result.data as Agent).name} added`);
    } catch {
      setToastMsg(t('pages.IvrAgents.failedToAddAgent'));
    } finally {
      setSaving(false);
    }
  };

  const handleEditStart = (agent: Agent) => {
    setEditAgent(agent);
    setEditName(agent.name);
    setEditPhone(agent.phone ?? '');
    setEditEmail(agent.email ?? '');
  };

  const handleEditSave = async () => {
    if (!editAgent) return;
    setEditSaving(true);
    try {
      await httpsCallable(functions, 'updateAgent')({
        workerSid: editAgent.sid,
        name: editName.trim(), phone: editPhone.trim(),
        email: editEmail.trim(),
      });
      setAgents(prev => prev.map(a => a.sid === editAgent.sid
        ? { ...a, name: editName.trim(), phone: editPhone.trim() || null, email: editEmail.trim() || null }
        : a
      ));
      setToastMsg(t('pages.IvrAgents.agentUpdated'));
      setEditAgent(null);
    } catch {
      setToastMsg(t('pages.IvrAgents.failedToUpdateAgent'));
    } finally {
      setEditSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    try {
      await setDoc(doc(dbTwilio, 'settings', 'ivr'), { greeting, voicemailPrompt }, { merge: true });
      setInitialGreeting(greeting);
      setInitialVoicemailPrompt(voicemailPrompt);
      setEditingGreeting(false);
      setEditingVoicemail(false);
      setToastMsg(t('pages.IvrAgents.phoneMessagesSaved'));
    } catch {
      setToastMsg(t('pages.IvrAgents.failedToSaveMessages'));
    } finally {
      setSettingsSaving(false);
    }
  };

  const availableCount = agents.filter(a => a.available).length;
  const hasMessageChanges =
    greeting.trim() !== initialGreeting.trim() ||
    voicemailPrompt.trim() !== initialVoicemailPrompt.trim();
  const activeMenuAgent = openMenuSid ? agents.find((a) => a.sid === openMenuSid) ?? null : null;

  const closeAgentMenu = useCallback(() => {
    setOpenMenuSid(null);
    setMenuPos(null);
  }, []);

  const openAgentMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>, sid: string) => {
    if (openMenuSid === sid) {
      closeAgentMenu();
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 172;
    const left = Math.max(8, rect.right - menuWidth);
    setMenuPos({ top: rect.bottom + 8, left });
    setOpenMenuSid(sid);
  }, [openMenuSid, closeAgentMenu]);

  return (
    <div className={styles.modalWrapper}>
      <div ref={pickerPortalRef} className={styles.pickerPortalRoot} />
      <BasePageWithHeader
        ref={baseHeaderRef}
        title={t('pages.IvrAgents.phoneAgents')}
        toolbarClassName={styles.toolbar}
        smallTitleClassName={styles.smallTitle}
        titleHeaderClassName={styles.titleHeader}
        startButtons={
          <IonButton fill="clear" className={styles.backButton} onClick={onClose}>
            <IonIcon icon={chevronBack} />
          </IonButton>
        }
        endButtons={canManageAll ? (
          <IonButton fill="clear" className={styles.refreshButton} onClick={() => { setShowAddForm(f => !f); setEditAgent(null); }}>
            <IonIcon icon={showAddForm ? closeOutline : addOutline} />
          </IonButton>
        ) : undefined}
      >
        <IonContent scrollY={false} className={styles.content}>
          <IonGrid
            ref={gridRef}
            className={styles.scrollContainer}
            onScroll={(e) => handleScroll((e.target as HTMLElement).scrollTop || 0)}
          >
          <div className={styles.scrollContent}>
            <TitleCard
              ref={titleCardRef}
              title={t('pages.IvrAgents.phoneAgents')}
              subtitle={t('pages.IvrAgents.manageWhoAnswersIncomingCalls')}
              subtitleClassName={styles.heroSubtitle}
              cardClassName={styles.titleCard}
              titleContainerClassName={styles.titleContainer}
              titleClassName={styles.titleText}
            />

            {/* Add form */}
            {canManageAll && showAddForm && (
              <IonCard className={styles.card}>
                <IonCardContent className={`${styles.section} ${styles.actionSection}`}>
                  <div className={styles.panelHeader}>
                    <div className={`${styles.iconBadge} ${styles.iconBadgeTertiary} ${styles.panelIconBadge}`}>
                      <IonIcon icon={addOutline} />
                    </div>
                    <span className={styles.panelTitle}>{t('pages.IvrAgents.addPhoneAgent')}</span>
                  </div>
                  <IonLabel className={styles.fieldBox}>
                    <input
                      type="text"
                      value={newName}
                      placeholder={t('pages.IvrAgents.name')}
                      className={styles.fieldInput}
                      onChange={(e) => setNewName(e.target.value)}
                    />
                  </IonLabel>
                  <PhoneAgentInput
                    value={newPhone}
                    onChange={setNewPhone}
                    portalRef={pickerPortalRef}
                    boxClassName={styles.fieldBox}
                  />
                  <IonLabel className={styles.fieldBox}>
                    <input
                      type="email"
                      value={newEmail}
                      placeholder={t('pages.IvrAgents.loginEmailForVoip')}
                      className={styles.fieldInput}
                      onChange={(e) => setNewEmail(e.target.value)}
                    />
                  </IonLabel>
                  <IonButton
                    className={styles.addBtn}
                    onClick={handleAdd}
                    disabled={saving || !newName.trim() || !newPhone.trim() || !newEmail.trim()}
                  >
                    {saving ? <IonSpinner name="bubbles" color="light" className={styles.btnSpinner} /> : t('pages.IvrAgents.addAgent')}
                  </IonButton>
                </IonCardContent>
              </IonCard>
            )}

            {/* Main card */}
            <IonCard className={styles.card}>

              {/* Agents section */}
              <IonCardContent className={styles.section}>
                <div className={styles.panelHeader}>
                  <div className={styles.panelHeaderLead}>
                    <div className={`${styles.iconBadge} ${styles.iconBadgeSecondary} ${styles.panelIconBadge}`}>
                      <IonIcon icon={callOutline} />
                    </div>
                    <span className={styles.panelTitle}>{t('pages.IvrAgents.currentPhoneAgents')}</span>
                  </div>
                  {loading ? (
                    <IonSpinner name="dots" color="primary" className={styles.loadingDot} />
                  ) : agents.length > 0 ? (
                    <IonChip className={`${styles.chip} ${styles[`chip_${availableCount === 0 ? 'medium' : availableCount === agents.length ? 'success' : 'warning'}`]}`}>
                      <IonLabel>{t('pages.IvrAgents.available', { availableCount, length: agents.length })}</IonLabel>
                    </IonChip>
                  ) : null}
                </div>

                {loading ? (
                  <div className={styles.loadingState}>
                    <IonSpinner name="bubbles" color="primary" />
                    <span>{t('pages.IvrAgents.loadingAgents')}</span>
                  </div>
                ) : agents.length === 0 ? (
                  <p className={styles.emptyHint}>{t('pages.IvrAgents.noPhoneAgentsConfigured')}</p>
                ) : (
                  <div className={styles.agentRows}>
                    {agents.map(agent => (
                      <React.Fragment key={agent.sid}>
                        <div className={styles.listRow}>
                          <div className={`${styles.iconBadge} ${styles.iconBadgePrimary} ${styles.rowIconBadge}`}>
                            <IonIcon icon={personOutline} />
                          </div>
                          <div className={styles.listRowText}>
                            <h3>{agent.name}</h3>
                            <p>{agent.phone || t('pages.IvrAgents.noPhone')} · {agent.activityName || t('pages.IvrAgents.noActivity')}</p>
                          </div>
                          <IonToggle
                            className={styles.rowToggle}
                            checked={agent.available}
                            disabled={!canManageAll && !agent.isSelf}
                            onIonChange={(e) => handleToggle(agent, e.detail.checked)}
                          />
                          {canManageAll && (<button
                            type="button"
                            className={styles.moreBtn}
                            onClick={(e) => openAgentMenu(e, agent.sid)}
                            aria-label={t('pages.IvrAgents.agentActions')}
                          >
                            <IonIcon icon={ellipsisVertical} />
                          </button>)}
                        </div>

                        {editAgent?.sid === agent.sid && (
                          <IonItem lines="none" className={styles.editSection}>
                            <IonLabel className={`${styles.editBody} ion-text-wrap`}>
                              <IonLabel className={styles.fieldBox}>
                                <input
                                  type="text"
                                  value={editName}
                                  placeholder={t('pages.IvrAgents.name')}
                                  className={styles.fieldInput}
                                  onChange={(e) => setEditName(e.target.value)}
                                />
                              </IonLabel>
                              <PhoneAgentInput
                                value={editPhone}
                                onChange={setEditPhone}
                                portalRef={pickerPortalRef}
                                boxClassName={styles.fieldBox}
                              />
                              <IonLabel className={styles.fieldBox}>
                                <input
                                  type="email"
                                  value={editEmail}
                                  placeholder={t('pages.IvrAgents.loginEmail')}
                                  className={styles.fieldInput}
                                  onChange={(e) => setEditEmail(e.target.value)}
                                />
                              </IonLabel>
                              <div className={styles.editActions}>
                                <IonButton className={styles.editCancelBtn} onClick={() => setEditAgent(null)}>{t('pages.IvrAgents.cancel')}
                                </IonButton>
                                <IonButton className={styles.editSaveBtn} onClick={handleEditSave} disabled={editSaving}>
                                  {editSaving ? <IonSpinner name="bubbles" color="light" className={styles.btnSpinner} /> : t('pages.IvrAgents.save')}
                                </IonButton>
                              </div>
                            </IonLabel>
                          </IonItem>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </IonCardContent>
            </IonCard>

            {/* Phone messages section */}
            {canManageAll && (<IonCard className={styles.card}>
              <IonCardContent className={`${styles.section} ${styles.actionSection}`}>
                <div className={styles.panelHeader}>
                  <div className={`${styles.iconBadge} ${styles.iconBadgePrimary} ${styles.panelIconBadge}`}>
                    <IonIcon icon={mailOutline} />
                  </div>
                  <span className={styles.panelTitle}>{t('pages.IvrAgents.phoneMessages')}</span>
                </div>
                <div className={styles.messageFieldHeader}>
                  <IonLabel className={styles.fieldLabel}>{t('pages.IvrAgents.greeting')}</IonLabel>
                  <button type="button" className={styles.inlineEditBtn} onClick={() => setEditingGreeting(v => !v)}>
                    {editingGreeting ? t('pages.IvrAgents.done') : t('pages.IvrAgents.edit')}
                  </button>
                </div>
                {editingGreeting ? (
                  <IonTextarea
                    rows={3}
                    value={greeting}
                    onIonInput={e => setGreeting(e.detail.value ?? '')}
                    placeholder={t('pages.IvrAgents.thankYouForCallingRS')}
                    className={styles.textArea}
                  />
                ) : (
                  <div className={`${styles.readOnlyValue} ${!greeting?.trim() ? styles.readOnlyEmpty : ''}`}>
                    {/* i18n-ignore: default greeting is what callers actually hear */}
                    {greeting?.trim() || "Thank you for calling R&S Maids. Please leave your name and number, and we will get back to you shortly. "}
                  </div>
                )}
                <div className={styles.messageFieldHeader}>
                  <IonLabel className={styles.fieldLabel}>{t('pages.IvrAgents.voicemailPrompt')}</IonLabel>
                  <button type="button" className={styles.inlineEditBtn} onClick={() => setEditingVoicemail(v => !v)}>
                    {editingVoicemail ? t('pages.IvrAgents.done') : t('pages.IvrAgents.edit')}
                  </button>
                </div>
                {editingVoicemail ? (
                  <IonTextarea
                    rows={3}
                    value={voicemailPrompt}
                    onIonInput={e => setVoicemailPrompt(e.detail.value ?? '')}
                    placeholder={t('pages.IvrAgents.sorryWeCouldnTGetTo')}
                    className={styles.textArea}
                  />
                ) : (
                  <div className={`${styles.readOnlyValue} ${!voicemailPrompt?.trim() ? styles.readOnlyEmpty : ''}`}>
                    {/* i18n-ignore: default prompt is what callers actually hear */}
                    {voicemailPrompt?.trim() || "Sorry we couldn't get to your call right now. Please leave your name, number, and a brief message, and we will call you back as soon as possible. "}
                  </div>
                )}
                <IonButton
                  className={styles.saveBtn}
                  onClick={handleSaveSettings}
                  disabled={settingsSaving || !hasMessageChanges}
                >
                  {settingsSaving ? <IonSpinner name="bubbles" color="light" className={styles.btnSpinner} /> : t('pages.IvrAgents.saveMessages')}
                </IonButton>
              </IonCardContent>
            </IonCard>)}
          </div>
          </IonGrid>
        </IonContent>

        {openMenuSid && menuPos && activeMenuAgent && createPortal(
          <>
            <div className={styles.agentMenuBackdrop} onClick={closeAgentMenu} />
            <IonCard
              className={styles.agentMenuList}
              style={{ top: `${menuPos.top}px`, left: `${menuPos.left}px` }}
            >
              <button
                type="button"
                className={styles.agentMenuItem}
                onClick={() => {
                  closeAgentMenu();
                  if (editAgent?.sid === activeMenuAgent.sid) {
                    setEditAgent(null);
                  } else {
                    handleEditStart(activeMenuAgent);
                  }
                }}
              >
                <IonIcon icon={editAgent?.sid === activeMenuAgent.sid ? closeOutline : createOutline} />
                <span>{editAgent?.sid === activeMenuAgent.sid ? t('pages.IvrAgents.closeEdit') : t('pages.IvrAgents.editAgent')}</span>
              </button>
              <button
                type="button"
                className={`${styles.agentMenuItem} ${styles.agentMenuItemDanger}`}
                onClick={() => {
                  closeAgentMenu();
                  setDeleteAlert(activeMenuAgent);
                }}
              >
                <IonIcon icon={trashOutline} />
                <span>{t('pages.IvrAgents.removeAgent')}</span>
              </button>
            </IonCard>
          </>,
          document.body,
        )}

        <IonAlert
          isOpen={!!deleteAlert}
          header={t('pages.IvrAgents.removeAgent')}
          message={t('pages.IvrAgents.removeFromPhoneAgents', { name: deleteAlert?.name })}
          buttons={[
            { text: t('pages.IvrAgents.cancel'), role: 'cancel', handler: () => setDeleteAlert(null) },
            { text: t('pages.IvrAgents.remove'), role: 'destructive', handler: () => { if (deleteAlert) handleDelete(deleteAlert); } },
          ]}
          onDidDismiss={() => setDeleteAlert(null)}
        />
        <IonToast isOpen={!!toastMsg} message={toastMsg} duration={2500} onDidDismiss={() => setToastMsg('')} />
      </BasePageWithHeader>
    </div>
  );
};

export default IvrAgents;
